import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Save, RefreshCw } from 'lucide-react';
import { toast } from 'react-toastify';
import { cogsAPI } from '../../services/api';
import { can } from '../../services/session';
import CostImport from './CostImport';

// Cost of goods per Article + Style (rate + freight per unit), used by the monthly reports.
export default function CogsRates() {
  const [rates, setRates] = useState(null);
  const [edits, setEdits] = useState({});
  const [onlyMissing, setOnlyMissing] = useState(false);
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(false);
  const canEdit = can('cogs', 'edit');

  const load = useCallback(async () => {
    try {
      const r = await cogsAPI.list();
      setRates(r.data.rates || []);
      setEdits({});
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not load COGS rates');
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const key = (r) => `${r.article}|${r.style}`;
  const value = (r, field) => (edits[key(r)] && field in edits[key(r)] ? edits[key(r)][field] : (r[field] ?? ''));
  const setValue = (r, field, v) => setEdits(e => ({ ...e, [key(r)]: { ...e[key(r)], [field]: v } }));

  const shown = useMemo(() => (rates || []).filter(r =>
    (!onlyMissing || (r.products > 0 && r.rate === null)) &&
    (!search || `${r.article} ${r.style}`.toLowerCase().includes(search.toLowerCase()))), [rates, onlyMissing, search]);

  const save = async () => {
    const changed = (rates || []).filter(r => edits[key(r)]).map(r => ({
      article: r.article, style: r.style, rate: value(r, 'rate'), freight: value(r, 'freight'), note: value(r, 'note'),
    }));
    if (!changed.length) return toast.info('Nothing changed');
    setBusy(true);
    try {
      await cogsAPI.save(changed);
      toast.success(`Saved ${changed.length} rate(s)`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not save');
    } finally {
      setBusy(false);
    }
  };

  if (!rates) return <div className="text-gray-400">Loading…</div>;
  const missing = rates.filter(r => r.products > 0 && r.rate === null).length;
  const input = 'bg-white/10 border border-white/20 rounded px-2 py-1 text-white w-24';

  return (
    <div className="max-w-5xl">
      <h1 className="text-3xl font-bold text-white mb-1">COGS Rates</h1>
      <p className="text-gray-300 mb-6">Cost per unit for each Article + Style (rate + freight). Used to work out COGS in the monthly reports.</p>

      {canEdit && <CostImport rates={rates} onDone={load} />}

      <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <input className="bg-white/10 border border-white/20 rounded px-3 py-2 text-white w-64" placeholder="Search article or style"
                 value={search} onChange={e => setSearch(e.target.value)} />
          <label className="text-gray-300 text-sm flex items-center gap-2">
            <input type="checkbox" checked={onlyMissing} onChange={e => setOnlyMissing(e.target.checked)} /> Only styles with no rate ({missing})
          </label>
          <div className="flex-1" />
          <button onClick={load} className="px-3 py-2 rounded bg-white/10 text-white flex items-center gap-2"><RefreshCw size={16} /> Reload</button>
          {canEdit && (
            <button onClick={save} disabled={busy || !Object.keys(edits).length}
                    className="px-4 py-2 rounded bg-white text-gray-900 font-semibold flex items-center gap-2 disabled:opacity-50">
              <Save size={16} /> Save changes{Object.keys(edits).length ? ` (${Object.keys(edits).length})` : ''}
            </button>
          )}
        </div>

        <table className="w-full text-sm">
          <thead>
            <tr className="text-gray-400 text-left border-b border-white/10">
              <th className="py-2">Article</th><th>Style</th><th className="text-right pr-4">Products</th>
              <th>Rate (£)</th><th>Freight (£)</th><th className="text-right pr-4">Cost / unit</th><th>Note</th>
            </tr>
          </thead>
          <tbody>
            {shown.map(r => {
              const noRate = r.products > 0 && value(r, 'rate') === '';
              const total = (Number(value(r, 'rate')) || 0) + (Number(value(r, 'freight')) || 0);
              return (
                <tr key={key(r)} className={`border-b border-white/5 ${noRate ? 'bg-red-500/10' : ''}`}>
                  <td className="py-2 text-white">{r.article}</td>
                  <td className="text-white">{r.style}</td>
                  <td className="text-right pr-4 text-gray-300">{r.products}</td>
                  <td>{canEdit ? <input type="number" step="0.01" min="0" className={input} value={value(r, 'rate')} onChange={e => setValue(r, 'rate', e.target.value)} /> : <span className="text-white">{r.rate ?? '—'}</span>}</td>
                  <td>{canEdit ? <input type="number" step="0.01" min="0" className={input} value={value(r, 'freight')} onChange={e => setValue(r, 'freight', e.target.value)} /> : <span className="text-white">{r.freight ?? '—'}</span>}</td>
                  <td className="text-right pr-4 text-white">{noRate ? <span className="text-red-300">no rate</span> : `£${total.toFixed(2)}`}</td>
                  <td>{canEdit ? <input className={input + ' w-56'} value={value(r, 'note')} onChange={e => setValue(r, 'note', e.target.value)} /> : <span className="text-gray-300">{r.note}</span>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {!shown.length && <p className="text-gray-400 mt-4">Nothing to show.</p>}
      </div>
    </div>
  );
}
